import type { SaveAccountCurrencyParams } from '@/entities/trading-platform';

import { baseApi, ENDPOINTS } from '@/shared/api';
import type { FileType } from '@/shared/types/antd';
import type { Currency } from '@/shared/types/currency';

import type { GetAccountCalendarDataRequestParams } from '../models/types/get-account-calendar-data-request-params';
import type { GetAccountCalendarDataResponse } from '../models/types/get-account-calendar-data-response';
import type { GetAccountCalendarNoteRequestParams } from '../models/types/get-account-calendar-note-request-params';
import type { GetAccountCalendarNoteResponse } from '../models/types/get-account-calendar-note-response';
import type { GetAccountInfoRequestParams } from '../models/types/get-account-info-request-params';
import type { GetAccountStatsRequestParams } from '../models/types/get-account-stats-request-params';
import type { GetAccountStatsResponse } from '../models/types/get-account-stats-response';
import type { GetBalanceHistoryRequestParams } from '../models/types/get-balance-history-request-params';
import type { GetBalanceHistoryResponse } from '../models/types/get-balance-history-response';
import type { GetLiveTradesRequestParams } from '../models/types/get-live-trades-request-params';
import type { GetLiveTradesResponse } from '../models/types/get-live-trades-response';
import type { SaveAccountCalendarNoteRequestParams } from '../models/types/save-account-calendar-note-request-params';
import type {
    TradingPlatformAccountInfo,
    TradingPlatformCTraderAccountInfo,
    TradingPlatformMT4AccountInfo,
    TradingPlatformMT5AccountInfo,
} from '../models/types/trading-platform-account-info';

const tradingPlatformApi = baseApi
    .enhanceEndpoints({
        addTagTypes: ['CalendarNote', 'AccountInfo'],
    })
    .injectEndpoints({
        endpoints: (build) => ({
            getAccountInfo: build.query<
                TradingPlatformAccountInfo,
                GetAccountInfoRequestParams
            >({
                providesTags: ['AccountInfo'],
                query: ({ accountId, connectionType }) => {
                    switch (connectionType) {
                        case 'mt4':
                            return ENDPOINTS.tradingPlatform.getMT4AccountInfo(
                                accountId,
                            );
                        case 'mt5':
                            return ENDPOINTS.tradingPlatform.getAccountInfo(
                                accountId,
                            );
                        default:
                            return ENDPOINTS.tradingPlatform.getCTraderAccountInfo(
                                accountId,
                            );
                    }
                },
                transformResponse: (
                    response:
                        | TradingPlatformMT5AccountInfo
                        | TradingPlatformMT4AccountInfo
                        | TradingPlatformCTraderAccountInfo,
                    _,
                    arg,
                ): TradingPlatformAccountInfo => {
                    switch (arg.connectionType) {
                        case 'mt4':
                            return {
                                login: +(
                                    response as TradingPlatformMT4AccountInfo
                                ).login,
                                currency: (
                                    response as TradingPlatformMT4AccountInfo
                                ).currency as Currency,
                            };
                        case 'mt5':
                            return {
                                login: +(
                                    response as TradingPlatformMT5AccountInfo
                                ).login,
                                currency: (
                                    response as TradingPlatformMT5AccountInfo
                                ).currency as Currency,
                            };
                        default:
                            return {
                                login: (
                                    response as TradingPlatformCTraderAccountInfo
                                ).account_id,
                                currency: (
                                    response as TradingPlatformCTraderAccountInfo
                                ).currency as Currency,
                            };
                    }
                },
            }),
            getAccountStats: build.query<
                GetAccountStatsResponse,
                GetAccountStatsRequestParams
            >({
                query: ({ id: accountId, rangeValue, connectionType }) => {
                    const [startDate, endDate] = rangeValue;

                    let url;

                    switch (connectionType) {
                        case 'mt4':
                            url =
                                ENDPOINTS.tradingPlatform.getMT4AccountStats(
                                    accountId,
                                );

                            break;
                        case 'mt5':
                            url =
                                ENDPOINTS.tradingPlatform.getAccountStats(
                                    accountId,
                                );

                            break;
                        default:
                            url =
                                ENDPOINTS.tradingPlatform.getCTraderAccountStats(
                                    accountId,
                                );
                    }

                    return {
                        url,
                        params: {
                            from_date: startDate,
                            to_date: endDate,
                        },
                    };
                },
            }),
            getBalanceHistory: build.query<
                GetBalanceHistoryResponse,
                GetBalanceHistoryRequestParams
            >({
                query: ({ accountId, connectionType, rangeValue }) => {
                    const [startDate, endDate] = rangeValue;

                    let url;

                    switch (connectionType) {
                        case 'mt4':
                            url =
                                ENDPOINTS.tradingPlatform.getMT4BalanceHistory(
                                    accountId,
                                );

                            break;
                        case 'mt5':
                            url =
                                ENDPOINTS.tradingPlatform.getBalanceHistory(
                                    accountId,
                                );

                            break;
                        default:
                            url =
                                ENDPOINTS.tradingPlatform.getCTraderBalanceHistory(
                                    accountId,
                                );
                    }

                    return {
                        url,
                        params: {
                            from_date: startDate,
                            to_date: endDate,
                        },
                    };
                },
            }),
            getLiveTrades: build.query<
                GetLiveTradesResponse,
                GetLiveTradesRequestParams
            >({
                query: ({ accountId, connectionType }) => {
                    switch (connectionType) {
                        case 'mt4':
                            return ENDPOINTS.tradingPlatform.getMT4LiveTrades(
                                accountId,
                            );
                        case 'mt5':
                            return ENDPOINTS.tradingPlatform.getLiveTrades(
                                accountId,
                            );
                        default:
                            return ENDPOINTS.tradingPlatform.getCTraderLiveTrades(
                                accountId,
                            );
                    }
                },
            }),
            getAccountCalendarData: build.query<
                GetAccountCalendarDataResponse,
                GetAccountCalendarDataRequestParams
            >({
                query: ({ id: accountId, date, connectionType }) => {
                    let url;

                    switch (connectionType) {
                        case 'mt4':
                            url =
                                ENDPOINTS.tradingPlatform.getMT4AccountCalendar(
                                    accountId,
                                );

                            break;
                        case 'mt5':
                            url =
                                ENDPOINTS.tradingPlatform.getAccountCalendar(
                                    accountId,
                                );

                            break;
                        default:
                            url =
                                ENDPOINTS.tradingPlatform.getCTraderAccountCalendar(
                                    accountId,
                                );
                    }

                    return {
                        url,
                        params: {
                            month_date: date,
                        },
                    };
                },
            }),
            getAccountCalendarNote: build.query<
                GetAccountCalendarNoteResponse,
                GetAccountCalendarNoteRequestParams
            >({
                query: ({ id: accountId, date, connectionType }) => {
                    let url;

                    switch (connectionType) {
                        case 'mt4':
                            url =
                                ENDPOINTS.tradingPlatform.getMT4AccountCalendarNoteByDate(
                                    accountId,
                                );

                            break;
                        case 'mt5':
                            url =
                                ENDPOINTS.tradingPlatform.getAccountCalendarNoteByDate(
                                    accountId,
                                );

                            break;
                        default:
                            url =
                                ENDPOINTS.tradingPlatform.getCTraderAccountCalendarNoteByDate(
                                    accountId,
                                );
                    }

                    return {
                        url,
                        params: {
                            date,
                        },
                    };
                },
                providesTags: ['CalendarNote'],
            }),
            saveAccountCalendarNote: build.mutation<
                void,
                SaveAccountCalendarNoteRequestParams
            >({
                query: ({ id: accountId, connectionType, ...body }) => {
                    let url;

                    switch (connectionType) {
                        case 'mt4':
                            url =
                                ENDPOINTS.tradingPlatform.saveMT4AccountCalendarNoteByDate(
                                    accountId,
                                );

                            break;
                        case 'mt5':
                            url =
                                ENDPOINTS.tradingPlatform.saveAccountCalendarNoteByDate(
                                    accountId,
                                );

                            break;
                        default:
                            url =
                                ENDPOINTS.tradingPlatform.saveCTraderAccountCalendarNoteByDate(
                                    accountId,
                                );
                    }

                    return {
                        url,
                        method: 'POST',
                        body,
                    };
                },
                invalidatesTags: ['CalendarNote'],
            }),
            uploadMT4Statement: build.mutation<
                void,
                { accountId: number; file: FileType | Blob }
            >({
                query: ({ accountId, file }) => {
                    const formData = new FormData();

                    formData.append('file', file);

                    return {
                        url: ENDPOINTS.tradingPlatform.uploadMT4Statement(
                            accountId,
                        ),
                        method: 'POST',
                        body: formData,
                    };
                },
            }),
            saveAccountCurrency: build.mutation<
                void,
                SaveAccountCurrencyParams
            >({
                invalidatesTags: ['AccountInfo'],
                query: ({ id: accountId, connectionType, ...body }) => {
                    let url;

                    switch (connectionType) {
                        case 'mt4':
                            url =
                                ENDPOINTS.tradingPlatform.saveMT4AccountCurrency(
                                    accountId,
                                );

                            break;
                        case 'mt5':
                            url =
                                ENDPOINTS.tradingPlatform.saveAccountCurrency(
                                    accountId,
                                );

                            break;
                        default:
                            url =
                                ENDPOINTS.tradingPlatform.saveCTraderAccountCurrency(
                                    accountId,
                                );
                    }

                    return {
                        url,
                        method: 'PATCH',
                        body,
                    };
                },
            }),
        }),
    });

export const {
    useLazyGetAccountStatsQuery,
    useLazyGetAccountInfoQuery,
    useLazyGetAccountCalendarDataQuery,
    useLazyGetAccountCalendarNoteQuery,
    useLazyGetBalanceHistoryQuery,
    useLazyGetLiveTradesQuery,
    useSaveAccountCalendarNoteMutation,
    useSaveAccountCurrencyMutation,
    useUploadMT4StatementMutation,
} = tradingPlatformApi;
